import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import useGlynt from './useGlynt';
import { addDocumentQueryParams } from '../documents/documentUtils';

type ParamTypes = {
  limit: number;
  dpId: string;
  defaultPage: number;
  labelContains?: string;
  hasBeenSent?: string;
  isSupported?: string;
  formatId?: string;
  createdAfter?: string;
  createdBefore?: string;
  includeName?: string;
  excludeName?: string;
  includeId?: string;
  excludeId?: string;
  includeLanguage?: string;
  excludeLanguage?: string;
  includeDuplicateKey?: string;
  excludeDuplicateKey?: string;
  includeDocumentDataStatus?: string;
  inRelationshipSetWith?: string;
  rootDocumentData?: string;
};
export default function usePaginatedDocumentsManifest({
  limit = 100,
  dpId,
  defaultPage,
  labelContains,
  hasBeenSent,
  isSupported,
  formatId,
  createdAfter,
  createdBefore,
  includeName,
  excludeName,
  includeId,
  excludeId,
  includeLanguage,
  excludeLanguage,
  includeDuplicateKey,
  excludeDuplicateKey,
  includeDocumentDataStatus,
  inRelationshipSetWith,
  rootDocumentData,
}: ParamTypes) {
  const [page, setPage] = useState(defaultPage || 0);
  const glynt = useGlynt(dpId);

  const queryParams = {
    limit,
    offset: page * limit,
    ...(inRelationshipSetWith && { in_relationship_set_with: inRelationshipSetWith }),
    ...(rootDocumentData && { in_lineage_with: rootDocumentData }),
    ...addDocumentQueryParams(
      labelContains,
      hasBeenSent,
      isSupported,
      formatId,
      createdAfter,
      createdBefore,
      includeName,
      excludeName,
      includeId,
      excludeId,
      includeLanguage,
      excludeLanguage,
      includeDuplicateKey,
      excludeDuplicateKey,
      includeDocumentDataStatus,
      undefined // excludeDocumentDataStatus not supported by API
    ),
  };

  const { data, isError, isPending, isFetching } = useQuery({
    queryKey: [
      'documents',
      // listUnscoped pins ?data_pool=<wrapper.dataPoolId> on the URL, so a key without it
      // serves one pool's page from another pool's cache entry. Pre-existing; included
      // here because this hook gained a filter in this branch.
      dpId,
      {
        page,
        limit,
        labelContains,
        hasBeenSent,
        isSupported,
        formatId,
        createdAfter,
        createdBefore,
        includeName,
        excludeName,
        includeId,
        excludeId,
        includeLanguage,
        excludeLanguage,
        includeDuplicateKey,
        excludeDuplicateKey,
        includeDocumentDataStatus,
        inRelationshipSetWith,
        rootDocumentData,
      },
    ],

    queryFn: async () => (await glynt).document.listUnscoped(queryParams),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData
  });

  const total = data ? Math.ceil(data.count / limit) : 1;

  const nextPage = () => {
    if (page < total - 1) setPage((page) => page + 1);
  };

  const previousPage = () => {
    if (page > 0) setPage((page) => page - 1);
  };

  return {
    data,
    error: isError,
    isPending,
    isLoading: isPending,
    isFetching,
    nextPage,
    previousPage,
    total,
    page,
    setPage,
  };
}
